/**
 * ScrollVideoNarrative — single sticky scroll-driven hero with TWO videos
 * stacked on top of each other, crossfading from V1 to V2 at the midpoint.
 *
 * Why this exists: previously V1 and V2 lived in two separate sticky
 * sections, which created a visible seam where the user had to scroll
 * past the bottom of V1 before V2 started sticking. That seam looked
 * awkward — both videos were visible at once for a moment.
 *
 * This component merges them into ONE sticky container with both videos
 * absolutely positioned on top of each other:
 *
 *   - The user scrolls through ONE big section (e.g. 700vh tall).
 *   - V1 is visible and scrubbing during the first half of scroll.
 *   - At the crossfade range (e.g. 48%–52%), V1 fades out and V2 fades in.
 *   - V2 is visible and scrubbing during the second half.
 *   - Both videos are seek-driven by the same scroll progress, but only
 *     one is visible at a time.
 *   - All five text cards are rendered in the same sticky container.
 *     Cards 1–3 use scroll ranges in [0, 0.48]. Cards 4–5 use ranges in
 *     [0.52, 1.0]. They naturally appear/disappear over the right video.
 *   - Optional bg transition near the end for the visual handoff into
 *     the dark marketing sections below.
 *
 * Locked spec: visual-quality skill §5 (Glass on hero overlay) and
 * Rule M7 (scroll-driven landing page).
 */

import { useEffect, useRef, useState } from "react";
import {
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useTransform,
  type MotionValue,
} from "framer-motion";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/cn";

/* ---------- Types ---------- */

export type CardSide = "left" | "right" | "center";

export type CardConfig = {
  /** i18n key prefix, e.g. "hero.card1" */
  key: string;
  /** Where the card sits over the video */
  side: CardSide;
  /** Scroll range [enterStart, holdStart, holdEnd, exitEnd] in 0..1 across the whole narrative */
  range: [number, number, number, number];
  /** Whether this card renders on the dark portion of the section */
  dark: boolean;
};

export type VideoLayer = {
  src: string;
  mobileSrc: string;
  poster: string;
};

export type ScrollVideoNarrativeProps = {
  /** Two video layers — V1 plays first, V2 plays after crossfade */
  videos: [VideoLayer, VideoLayer];
  /** All cards (V1 + V2) with scroll ranges in 0..1 across the whole narrative */
  cards: CardConfig[];
  /** Total scroll height in vh — controls how much scroll distance the narrative covers */
  heightVh: number;
  /** Scroll progress range [start, end] where V1 fades out and V2 fades in */
  crossfadeRange: [number, number];
  /** Optional background color transition (light → dark) over a scroll range */
  bgTransition?: {
    range: [number, number, number];
  };
  /** Show a small "scroll down" hint at the very start */
  showScrollHint?: boolean;
};

/* ---------- Persistent video cache (Cache Storage API) ----------
 *
 * The HTTP cache (cache-control: immutable on /hero/*.mp4) handles
 * normal refreshes for free, but a *hard* refresh (Cmd+Shift+R)
 * deliberately bypasses it. To make hard-refresh ALSO instant, we
 * stash the downloaded blob in the Cache Storage API — a separate
 * persistent store that the browser does not blow away on hard
 * refresh, tab close, or even browser restart.
 *
 * Bump HERO_CACHE_VERSION any time the mp4 contents change (re-encodes,
 * new files, anything). The eviction effect below deletes any cache
 * keyed under an older version on mount, so users get the new bytes
 * the next time they load the page.
 */
const HERO_CACHE_VERSION = "v1";
const HERO_CACHE_NAME = `vegr-hero-${HERO_CACHE_VERSION}`;

async function getCachedVideoBlob(url: string): Promise<Blob> {
  // Browsers without Cache Storage (very old or privacy-locked) just
  // fall through to a plain fetch — the page still works, it's only
  // missing the persistent-cache acceleration.
  if (typeof caches === "undefined") {
    const response = await fetch(url);
    return response.blob();
  }
  try {
    const cache = await caches.open(HERO_CACHE_NAME);
    const cached = await cache.match(url);
    if (cached) {
      return cached.blob();
    }
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    // `Response.body` is a stream that can only be read once, so we
    // clone it before consuming. Cache write failures are non-fatal —
    // we still want the user to get the bytes even if the cache put
    // gets denied (storage quota, private mode, etc).
    cache.put(url, response.clone()).catch(() => {});
    return response.blob();
  } catch {
    // Cache Storage misbehaved for any reason — fall back to direct
    // fetch so the page never breaks because of caching.
    const response = await fetch(url);
    return response.blob();
  }
}

async function evictStaleHeroCaches(): Promise<void> {
  if (typeof caches === "undefined") return;
  try {
    const names = await caches.keys();
    await Promise.all(
      names
        .filter((n) => n.startsWith("vegr-hero-") && n !== HERO_CACHE_NAME)
        .map((n) => caches.delete(n)),
    );
  } catch {
    // ignore
  }
}

/* ---------- Hook: tiny media-query helper ---------- */

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);

  return matches;
}

/* ---------- Card overlay ---------- */

type TextCardProps = {
  config: CardConfig;
  scrollYProgress: MotionValue<number>;
};

function TextCard({ config, scrollYProgress }: TextCardProps) {
  const { t } = useTranslation();
  const [enter, holdIn, holdOut, exit] = config.range;

  const opacity = useTransform(scrollYProgress, [enter, holdIn, holdOut, exit], [0, 1, 1, 0]);
  const y = useTransform(scrollYProgress, [enter, holdIn, holdOut, exit], [24, 0, 0, -24]);

  const positionClass =
    config.side === "left"
      ? "left-6 md:left-12 lg:left-20"
      : config.side === "right"
        ? "right-6 md:right-12 lg:right-20"
        : "left-1/2 -translate-x-1/2";

  return (
    <motion.div
      style={{ opacity, y }}
      className={cn(
        "pointer-events-none absolute top-1/2 z-20 -translate-y-1/2",
        positionClass,
      )}
    >
      {/* Frosted glass card — see visual-quality skill §5 (Glass on hero overlay).
          Bumped to 85% opacity for stronger text legibility while still
          reading as translucent glass over the busy video background. */}
      <div
        className={cn(
          "flex w-[20rem] flex-col items-center gap-5 rounded-[2rem] border p-8 text-center backdrop-blur-2xl md:w-[26rem] md:gap-6 md:p-10",
          config.dark
            ? "border-slate-700/60 bg-slate-900/85 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.08)]"
            : "border-white/85 bg-white/85 shadow-[0_24px_60px_-12px_rgba(15,23,42,0.22),inset_0_1px_0_rgba(255,255,255,0.9)]",
        )}
      >
        <span
          className={cn(
            "rounded-full border px-3 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.22em]",
            config.dark
              ? "border-slate-700/80 bg-slate-800/90 text-slate-300"
              : "border-white/90 bg-white text-slate-500",
          )}
        >
          {t(`${config.key}.tag`)}
        </span>

        <h2
          className={cn(
            "text-3xl font-bold leading-[1.1] tracking-tight md:text-4xl",
            config.dark ? "text-slate-50" : "text-slate-900",
          )}
        >
          {t(`${config.key}.title`)}
        </h2>

        <p
          className={cn(
            "text-sm leading-relaxed md:text-base",
            config.dark ? "text-slate-300" : "text-slate-600",
          )}
        >
          {t(`${config.key}.body`)}
        </p>
      </div>
    </motion.div>
  );
}

/* ---------- Reduced-motion fallback ---------- */

function ReducedMotionFallback({
  posters,
  cards,
}: {
  posters: [string, string];
  cards: CardConfig[];
}) {
  const { t } = useTranslation();
  return (
    <section className="relative bg-slate-50 py-20">
      <div className="mx-auto max-w-3xl space-y-16 px-6">
        <img
          src={posters[0]}
          alt=""
          width={1920}
          height={1080}
          className="w-full rounded-2xl border border-slate-200"
        />
        {cards.slice(0, Math.ceil(cards.length / 2)).map((card) => (
          <div key={card.key} className="flex flex-col gap-4">
            <span className="font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-slate-500">
              {t(`${card.key}.tag`)}
            </span>
            <h2 className="text-4xl font-bold leading-tight tracking-tight text-slate-900 md:text-5xl">
              {t(`${card.key}.title`)}
            </h2>
            <p className="max-w-[42ch] text-base leading-relaxed text-slate-600">
              {t(`${card.key}.body`)}
            </p>
          </div>
        ))}

        <img
          src={posters[1]}
          alt=""
          width={1920}
          height={1080}
          className="w-full rounded-2xl border border-slate-200"
        />
        {cards.slice(Math.ceil(cards.length / 2)).map((card) => (
          <div key={card.key} className="flex flex-col gap-4">
            <span className="font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-slate-500">
              {t(`${card.key}.tag`)}
            </span>
            <h2 className="text-4xl font-bold leading-tight tracking-tight text-slate-900 md:text-5xl">
              {t(`${card.key}.title`)}
            </h2>
            <p className="max-w-[42ch] text-base leading-relaxed text-slate-600">
              {t(`${card.key}.body`)}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- Main component ---------- */

export function ScrollVideoNarrative({
  videos,
  cards,
  heightVh,
  crossfadeRange,
  bgTransition,
  showScrollHint = false,
}: ScrollVideoNarrativeProps) {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const video1Ref = useRef<HTMLVideoElement>(null);
  const video2Ref = useRef<HTMLVideoElement>(null);

  const isMobile = useMediaQuery("(max-width: 768px)");
  const prefersReducedMotion = useReducedMotion();

  // Splash gate — `false` until both hero videos are downloaded into
  // blob URLs (or the safety timeout fires). When false, a full-screen
  // overlay covers the entire page so the user never sees a half-loaded
  // hero with poster + missing video. The user explicitly asked for
  // "2-3 second loading then reveal" instead of the previous behavior
  // where the page rendered immediately and videos faded in late.
  const [videosReady, setVideosReady] = useState(false);

  // Resolve responsive video sources up-front so the blob-loading effect
  // below can depend on them and React picks the right file per viewport.
  const v1Src = isMobile ? videos[0].mobileSrc : videos[0].src;
  const v2Src = isMobile ? videos[1].mobileSrc : videos[1].src;

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  // Crossfade midpoint — the boundary between V1 and V2 territory.
  // V1 plays during [0, crossfadeMid], V2 plays during [crossfadeMid, 1].
  const crossfadeMid = (crossfadeRange[0] + crossfadeRange[1]) / 2;

  // Drive BOTH videos' currentTime from the SAME scroll progress.
  // V1 maps [0, crossfadeMid] → [0, v1.duration]; clamped after.
  // V2 maps [crossfadeMid, 1] → [0, v2.duration]; clamped before.
  useMotionValueEvent(scrollYProgress, "change", (progress) => {
    const v1 = video1Ref.current;
    const v2 = video2Ref.current;

    if (v1 && v1.duration && !Number.isNaN(v1.duration)) {
      const normalized = Math.min(1, Math.max(0, progress / crossfadeMid));
      v1.currentTime = normalized * v1.duration;
    }
    if (v2 && v2.duration && !Number.isNaN(v2.duration)) {
      const normalized = Math.min(
        1,
        Math.max(0, (progress - crossfadeMid) / (1 - crossfadeMid)),
      );
      v2.currentTime = normalized * v2.duration;
    }
  });

  // Pause both videos on mount — we drive playback via scroll, not autoplay.
  useEffect(() => {
    [video1Ref.current, video2Ref.current].forEach((v) => {
      if (!v) return;
      v.pause();
      v.currentTime = 0;
    });
  }, []);

  // Evict any older `vegr-hero-*` cache buckets the moment the
  // component mounts. Bumping HERO_CACHE_VERSION (above) when we
  // re-encode the mp4s relies on this to free the stale bytes.
  useEffect(() => {
    void evictStaleHeroCaches();
  }, []);

  // Preload BOTH videos as in-memory Blobs and feed them to the <video>
  // elements via blob: URLs. This is THE fix for the scroll-scrub
  // cancellation cascade we hit on production:
  //
  //   With preload="metadata" + a streaming HTTP src, every scroll tick
  //   called video.currentTime = X, which the browser interpreted as a
  //   seek and turned into a fresh HTTP range request. The next scroll
  //   tick canceled the previous request, the next canceled that, and so
  //   on — DevTools showed 90+ canceled requests for a single scroll
  //   gesture, with only 1-2 ranges actually resolving. Result: 10-15s
  //   of frozen scrub on first load while the browser flailed.
  //
  //   Once the file is in a blob: URL, ALL seeks are local (the bytes
  //   live in JS memory). Zero network during scroll. Period. This is
  //   what Apple, Stripe, and Linear do for scroll-scrub video.
  //
  // The HTML <head> includes `<link rel="preload" as="fetch">` for these
  // same URLs, so the fetch() below is normally a memory/disk-cache hit
  // and the blob is ready before the user has scrolled past the hero.
  // While the blob is loading, the poster image (which IS frame 0)
  // remains visible — no spinner, no visible loading state.
  useEffect(() => {
    let cancelled = false;
    let url1: string | null = null;
    let url2: string | null = null;

    // Safety net — never trap the user behind the splash longer than 12s,
    // even if the network is broken or one of the fetches stalls. After
    // the timeout we reveal the page anyway and the hero degrades
    // gracefully (posters visible, scrub no-ops until bytes arrive).
    const fallbackTimer = window.setTimeout(() => {
      if (!cancelled) setVideosReady(true);
    }, 12000);

    (async () => {
      try {
        // `getCachedVideoBlob` checks Cache Storage first, falls
        // through to network on miss, and stores the response for
        // next time. On a return visit (including hard refresh) this
        // resolves in milliseconds with zero network traffic.
        const [b1, b2] = await Promise.all([
          getCachedVideoBlob(v1Src),
          getCachedVideoBlob(v2Src),
        ]);
        if (cancelled) return;
        url1 = URL.createObjectURL(b1);
        url2 = URL.createObjectURL(b2);
        const v1 = video1Ref.current;
        const v2 = video2Ref.current;
        if (v1) {
          v1.src = url1;
          v1.load();
        }
        if (v2) {
          v2.src = url2;
          v2.load();
        }
        // Both blobs in memory, video.src wired up. Reveal the page.
        // We don't wait for `loadeddata` events because the bytes are
        // already local — the seek-driven scrub works as soon as the
        // motion handler reads `v.duration`, which is available within
        // a microtask of `load()`.
        setVideosReady(true);
        window.clearTimeout(fallbackTimer);
      } catch {
        // Network failed — reveal anyway so the rest of the page is
        // usable. The hero will fall back to its poster image.
        setVideosReady(true);
      }
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(fallbackTimer);
      if (url1) URL.revokeObjectURL(url1);
      if (url2) URL.revokeObjectURL(url2);
    };
  }, [v1Src, v2Src]);

  // Cinematic crossfade — combines opacity, scale, and blur for a "punch
  // through" effect. V1 zooms in slightly and blurs as it fades out, V2
  // starts slightly smaller and blurred and resolves into focus as it
  // fades in. The combined effect feels like the camera continues moving
  // forward THROUGH the transition rather than cutting between two clips.
  //
  // Each transform interpolates across [0, crossfadeStart, crossfadeMid, crossfadeEnd, 1].
  // The midpoint frame uses a peak blur value so the transition has a
  // visible "blur peak" that the eye registers as a moment of motion.
  const crossfadeKeyframes: [number, number, number, number, number] = [
    0,
    crossfadeRange[0],
    (crossfadeRange[0] + crossfadeRange[1]) / 2,
    crossfadeRange[1],
    1,
  ];

  // V1: visible → fading & zooming forward → invisible
  const v1Opacity = useTransform(scrollYProgress, crossfadeKeyframes, [1, 1, 0.4, 0, 0]);
  const v1Scale = useTransform(scrollYProgress, crossfadeKeyframes, [1, 1, 1.05, 1.12, 1.12]);
  const v1Filter = useTransform(scrollYProgress, crossfadeKeyframes, [
    "blur(0px)",
    "blur(0px)",
    "blur(6px)",
    "blur(12px)",
    "blur(12px)",
  ]);

  // V2: invisible & zoomed-back → fading & zooming forward into focus → visible
  const v2Opacity = useTransform(scrollYProgress, crossfadeKeyframes, [0, 0, 0.4, 1, 1]);
  const v2Scale = useTransform(scrollYProgress, crossfadeKeyframes, [0.88, 0.88, 0.95, 1, 1]);
  const v2Filter = useTransform(scrollYProgress, crossfadeKeyframes, [
    "blur(12px)",
    "blur(12px)",
    "blur(6px)",
    "blur(0px)",
    "blur(0px)",
  ]);

  // Optional light → dark background transition near the end.
  const backgroundColor = useTransform(
    scrollYProgress,
    bgTransition?.range ?? [0, 0.5, 1],
    bgTransition
      ? ["#f8fafc", "#f8fafc", "#020617"] // slate-50 → slate-50 → slate-950
      : ["#f8fafc", "#f8fafc", "#f8fafc"],
  );

  // Scroll hint visibility (only at the very start)
  const scrollHintOpacity = useTransform(scrollYProgress, [0, 0.04], [showScrollHint ? 1 : 0, 0]);

  if (prefersReducedMotion) {
    return <ReducedMotionFallback posters={[videos[0].poster, videos[1].poster]} cards={cards} />;
  }

  return (
    <>
      {/* Splash gate — full-viewport overlay covering header, hero, and
          everything below. Stays opaque until both hero videos are
          downloaded into blob URLs (or the 12s safety timer fires), then
          fades out over 600ms.

          No spinner, no progress bar — the user explicitly said "no
          pager". Just a clean slate-50 sheet with the Vegr wordmark
          centered for brand presence, then a smooth reveal of a fully
          loaded page. z-index is well above the Header (which uses z-50)
          so nothing peeks through. The body's `bg-slate-50` matches this
          color exactly so the FOIT moment between HTML parse and React
          mount is invisible. */}
      <div
        aria-hidden
        className={cn(
          "fixed inset-0 z-[200] flex items-center justify-center bg-slate-50 transition-opacity duration-[600ms] ease-out",
          videosReady
            ? "pointer-events-none opacity-0"
            : "pointer-events-auto opacity-100",
        )}
      >
        <span className="select-none font-sans text-2xl font-semibold tracking-tight text-slate-300 md:text-3xl">
          Vegr
        </span>
      </div>

      <motion.section
        ref={sectionRef}
        style={{ backgroundColor }}
        className="relative"
      >
      <div style={{ height: `${heightVh}vh` }}>
        <motion.div
          style={{ backgroundColor }}
          className="sticky top-0 h-screen w-full overflow-hidden"
        >
          {/* Video layer 1 (source) — opacity + scale + blur controlled by
              scroll for cinematic crossfade.

              NOTE: there is NO `src` or `key` attribute here on purpose. The
              effect above fetches the file as a Blob and assigns the resulting
              `blob:` URL directly to `video.src` via the ref. Until that
              finishes, the poster (which IS frame 0) remains visible. See the
              big comment on the blob-loading effect for the full story. */}
          <motion.video
            ref={video1Ref}
            poster={videos[0].poster}
            muted
            playsInline
            preload="metadata"
            disablePictureInPicture
            style={{
              opacity: v1Opacity,
              scale: v1Scale,
              filter: v1Filter,
              willChange: "opacity, transform, filter",
              imageRendering: "high-quality" as unknown as "auto",
            }}
            className="absolute inset-0 h-full w-full object-cover"
          />

          {/* Video layer 2 (destination) — same cinematic transforms in reverse.
              Same `src`-via-blob trick as layer 1. */}
          <motion.video
            ref={video2Ref}
            poster={videos[1].poster}
            muted
            playsInline
            preload="metadata"
            disablePictureInPicture
            style={{
              opacity: v2Opacity,
              scale: v2Scale,
              filter: v2Filter,
              willChange: "opacity, transform, filter",
              imageRendering: "high-quality" as unknown as "auto",
            }}
            className="absolute inset-0 h-full w-full object-cover"
          />

          {/* Text cards — all in the same sticky container, each with its own range */}
          {cards.map((card) => (
            <TextCard key={card.key} config={card} scrollYProgress={scrollYProgress} />
          ))}

          {/* Scroll hint at the very start */}
          {showScrollHint && (
            <motion.div
              style={{ opacity: scrollHintOpacity }}
              className="pointer-events-none absolute bottom-10 left-1/2 z-20 flex -translate-x-1/2 flex-col items-center gap-2 text-slate-600"
            >
              <span className="font-mono text-[11px] uppercase tracking-[0.22em]">
                {t("common.scrollHint")}
              </span>
              <ChevronDown className="h-4 w-4 animate-bounce" aria-hidden />
            </motion.div>
          )}
        </motion.div>
      </div>
      </motion.section>
    </>
  );
}
